import React, { useState } from 'react';

import { Button, Col, Row, Tab, Tabs } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDebounce } from 'use-debounce';

import {
  backgroundGrey5,
  blackberry,
  coolGrey,
  grape,
  greyStroke,
  purple1,
  squidInk,
  white,
} from 'lib/css/colors';
import Container from 'components/shared/Container';
import ProductListTable from 'components/product_lists/product_list/ProductListTable';
import SearchBar from 'components/shared/SearchBar';

const PRODUCT_LIST_TABS = {
  DISTRIBUTOR: 'DISTRIBUTOR',
  MANUFACTURER: 'MANUFACTURER',
  BRAND: 'BRAND',
};

const TAB_LABELS = {
  [PRODUCT_LIST_TABS.DISTRIBUTOR]: 'Distributor',
  [PRODUCT_LIST_TABS.MANUFACTURER]: 'Manufacturer',
  [PRODUCT_LIST_TABS.BRAND]: 'Brand',
};

function ProductListsView() {
  const [activeTabKey, setActiveTabKey] = useState(PRODUCT_LIST_TABS.DISTRIBUTOR);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [debouncedQuery] = useDebounce(searchString, 600);

  return (
    <div className={css(styles.pageContainer)}>
      <div className={css(styles.headerSection)}>
        <div className={css(styles.headerContent)}>
          <h1 className={css(styles.pageTitle)}>Product Lists</h1>
        </div>
      </div>

      <Container className={css(styles.mainContent)}>
        <Row className={css(styles.toolbarRow)}>
          <Col xs={6} className="d-flex align-items-center">
            <div className={css(styles.searchWrapper)}>
              <SearchBar
                value={searchString}
                setValue={setSearchString}
                placeholder="Search product lists"
              />
            </div>
          </Col>
          <Col xs={6} className={css(styles.actionButtonsCol)}>
            <Button
              className={css(styles.primaryButton)}
              onClick={() => setShowCreateListModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              New List
            </Button>
          </Col>
        </Row>

        <Tabs
          activeKey={activeTabKey}
          onSelect={selectedKey => setActiveTabKey(selectedKey)}
          className={classNames(css(styles.tabs), 'mt-2')}
        >
          {Object.values(PRODUCT_LIST_TABS).map(tabKey => (
            <Tab key={tabKey} eventKey={tabKey} title={TAB_LABELS[tabKey]}>
              <div className={css(styles.scrollableContainer)}>
                <ProductListTable
                  productListType={tabKey}
                  searchString={debouncedQuery}
                  showCreateListModal={activeTabKey === tabKey && showCreateListModal}
                  setShowCreateListModal={setShowCreateListModal}
                />
              </div>
            </Tab>
          ))}
        </Tabs>
      </Container>
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: backgroundGrey5,
  },
  headerSection: {
    background: `linear-gradient(135deg, ${blackberry} 0%, ${purple1} 100%)`,
    padding: '24px 40px',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: 1600,
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 600,
    color: white,
    margin: 0,
  },
  mainContent: {
    padding: '24px 40px',
  },
  toolbarRow: {
    marginBottom: 16,
    alignItems: 'center',
  },
  searchWrapper: {
    flex: 1,
    maxWidth: 320,
  },
  actionButtonsCol: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  primaryButton: {
    backgroundColor: blackberry,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: grape,
    },
  },
  tabs: {
    borderBottom: `1px solid ${greyStroke}`,
    ':nth-child(1n) > .nav-item': {
      marginBottom: -1,
    },
    ':nth-child(1n) .nav-link': {
      color: coolGrey,
      fontWeight: 600,
      fontSize: 14,
      border: 'none',
      borderBottom: '3px solid transparent',
      borderRadius: 0,
      padding: '12px 18px',
      backgroundColor: 'transparent',
      transition: 'color 0.15s ease, border-color 0.15s ease',
    },
    ':nth-child(1n) .nav-link:hover': {
      color: grape,
      borderBottomColor: `${grape}55`,
    },
    ':nth-child(1n) .nav-link.active': {
      color: squidInk,
      backgroundColor: 'transparent',
      borderBottom: `3px solid ${grape}`,
    },
  },
  scrollableContainer: {
    backgroundColor: white,
    borderRadius: 12,
    border: `1px solid ${greyStroke}`,
    overflow: 'auto',
    marginTop: 12,
  },
});

export default ProductListsView;
